import { gql } from '@apollo/client';
import { creditApplicationOwnerFragment } from './fragments';

export const Q_VO = gql`
  query GetVO($voId: ID!) {
    vendorOpportunity(id: $voId) {
      accountProfileContact {
        fullName
      }
      closeInfo {
        id
        reasonId
        isClosed
        closeDealStatus
        reasonLabel
        closedDateTime
      }
      transactionId
      applicationStage
      accountPrimaryContactName
      accountPrimaryContactId
      createdOn
      statusDetail
      deliveryDate
      deliveryStatus
      downPayment
      equipmentApprovalStatus
      equipmentAcceptanceDate
      equipmentDescription
      equipmentCondition
      equipmentCost
      equipmentModelYear
      expectedDeliveryDate
      freightLaborInstallation
      invoiceStatus
      invoiceTotal
      orderDate
      dealerMarkUpRate
      dealerMarkUpAmount
      partnerProfileDynamicsId
      partnerLogo
      partnerName
      creditLineAmountRequested
      creditLineStatus
      creditLineNotes
      vendorProfileId
      sourceVendorProfileId
      extendedWarrantyAmount
      gapInsuranceAmount
      roadsideCoverageAmount
      roadHazardAmount
      paymentType
      docFeeAmount
      physicalDamageCoverageAmount
      federalExciseTaxAmount
      lengthOfContractInMonths
      checklist
      potentialCustomer {
        name
        id
        dynamicsAccountId
      }
      salesManager {
        id
        fullName
        dynamicsContactId
        firstName
        lastName
        email
        phoneNumber
        title
        status
      }
      salesRepresentative {
        id
        fullName
        dynamicsContactId
        firstName
        lastName
        email
        phoneNumber
        title
        status
      }
      salesTaxOnInvoice
      invoiceWarrantyAndInsuranceCharges
      manufacturerRebateAmount
      tradeOwedBalanceAmount
      tradeAllowanceGrossTradeIn
      vendorOpportunityId
      dynamicsVendorOpportunityId
      financeManager {
        id
        email
        dynamicsContactId
        firstName
        lastName
        fullName
        phoneNumber
        title
        status
      }
      dynamicsVOStatus
      dynamicsVOStage
      transactionStage
      voProposal {
        productId
        rate
        riskTier
        markupDealer
        amountRequested
        paymentCards {
          productName
          productHeader
          term
          rate
          payment
          rateFactor
          riskTier
          amountMin
          amountMax
          docFee
          advancePayment
          markupInternal
          productId
        }
      }
      contactFormNotes
      sourceUrl
      location {
        locationId
        locationName
        apiIdentifierEngs
        apiIdentifierPaccar
        category
        parentLocation {
          locationId
          locationName
          category
        }
      }
      fastTrackResults {
        creditSubmissionId
        fastTrackId
        applicationStatus
        rate
        maxApprovalAmount
        approvedBy
        inputs {
          key
          value
        }
        outputs {
          key
          value
        }
      }
      fastTrackStatus
      creditTiersResult {
        creditTiersResultId
        vendorOpportunityId
        prescreenCriteriaId
        financeProgramId
        runtimeInput
        runtimeOutput
        result
        highestTierPassed
        tiersType
        highestTierColor
        createdDateTime
        modifiedDateTime
      }
      notes
      entityType
      externalCrmVendorOpportunity {
        externalCrmVendorOpportunityId
        vendorOpportunityId
        externalId
        externalCrmSource
        data
        createdDateTime
        modifiedDateTime
      }
      businessProcessFlows {
        flows {
          businessProcessFlowId
        }
      }
      equipments {
        id
        year
        mileage
        make
        model
        newUsed
        retailPrice
        classType
        serialNumberVin
      }
      scoreCardResult {
        creditTiersResultId
        vendorOpportunityId
        prescreenCriteriaId
        financeProgramId
        runtimeInput
        runtimeOutput
        result
        highestTierPassed
        tiersType
        highestTierColor
        createdDateTime
        modifiedDateTime
      }
      creditLineRequest {
        creditLineRequestId
        vendorOpportunityId
        applicantAccountId
        applicantUserProfileId
        dealerAccountId
        source
        lenderProfileId
        requestedAmount
        approvedAmount
        status
        expirationDate
        notes
        createdAt
        updatedAt
      }
    }
  }
`;

export const M_CREATE_CREDIT_LINE_REQUEST = gql`
  mutation CreateCreditLineRequest($input: CreateCreditLineRequestInput!) {
    creditLineRequest {
      createCreditLineRequest(input: $input) {
        creditLineRequestId
        vendorOpportunityId
      }
    }
  }
`;

export const M_UPDATE_CREDIT_LINE_REQUEST = gql`
  mutation UpdateCreditLineRequest($input: UpdateCreditLineRequestInput!) {
    creditLineRequest {
      updateCreditLineRequest(input: $input) {
        creditLineRequestId
        vendorOpportunityId
        applicantAccountId
        applicantUserProfileId
        dealerAccountId
        source
        lenderProfileId

        requestedAmount
        approvedAmount

        status

        expirationDate
        notes

        createdAt
        updatedAt
      }
    }
  }
`;

export const Q_CREDIT_APP = gql`
  query CreditApplication($VOId: ID!) {
    creditApplication(VOId: $VOId) {
      id
      dynamicsId
      amountRequested
      firstName
      middleName
      lastName
      nameSuffix
      email
      businessName
      title
      phone
      bizPhone
      businessAddressCounty
      address
      city
      state
      stateOfIncorp
      postalCode
      legalStructure
      tib
      repId
      vendorSalespersonId
      transactionGuid
      yearsLicensed
      signature
      title
      ssn
      ssnExempt
      ownerPercentage
      businessType
      parentCompany
      country
      dateEstablished
      dateIncorporated
      taxId
      monthlyRevenue
      dateOfBirth
      industryExpYears
      monthlyIncome
      previousFinanceExp
      trucksOwned
      bizPhone
      homeSameAsBiz
      homeAddress
      homeCity
      homeState
      homePostalCode
      equipmentDescription
      createdDateTime
      modifiedDateTime
      paymentSelectorURL
      dcrType
      dcrProductId
      paymentSelectorURLV2
      proposalId
      rate
      riskTier
      markupInternal
      markupDealer
      dba
      vendorGUID
      stockId
      stateOfIncorp
      businessAddressCounty
      driversLicenseNum
      dlStateIssued
      homeType
      homePhone
      applicationType
      applyingFor
      creditLineAmountRequested
      creditLineStatus
      creditLineNotes
      sic
      equipmentCategoryCode
      equipmentNumInFleet
      purchaseReason
      piecesOfHeavyEqpt
      piecesOfMediumEqpt
      piecesOfTrailerEqpt
      totalEqpt
      eqptUse
      numOfOwnerOps
      typesOfGoods
      longHaulRegional
      hazmat
      yearMakeModel
      payment
      eqptPaidOff
      eqptTrade
      ownAuth
      mcNumber
      usDotNum
      eqptAddOrReplace
      applyingFor
      invoiceDelivery
      purchaseOrder
      primaryAgriculturalProduct
      farmingStartYear
      farmingIncome
      nonFarmingIncome
      nearestRelativeFirstName
      nearestRelativeLastName
      nearestRelativeCity
      nearestRelativePhone
      nearestRelativeState
      nearestRelativeZip
      billingAddress
      billingCity
      billingState
      billingPostalCode
      numberOfEmployees
      useByApplicant
      kubotaPrincipalUse
      medicalPractice
      industryType
      taxId
      createdDateTime
      entityType
      needsRecertification
      ocaTemplateId
      creditDisclosure
      isCreditDisclosureAccepted
      offerSummarySignature
      offerSummarySignatureDate
      offerSummaryDisclosure
      ocaTemplate {
        vendorProfileId
        jsonDefinition
        name
      }
      location {
        locationId
        locationName
        category
        accountDynamicsId
        apiIdentifierEngs
        apiIdentifierPaccar
        contactIds
      }
      primaryContact {
        ownerPgId
        pc
        pg
        pcOnly
        isContactOnly
        isMarketingApproved
        firstName
        middleName
        lastName
        nameSuffix
        ownerPercentage
        ssn
        title
        dateOfBirth
        address
        address2
        city
        state
        postalCode
        addressCounty
        driversLicenseNum
        dlStateIssued
        driverLicenseExp
        ownerNumber
        homeType
        homePhone
        phone
        timeAtAddressYears
        timeAtAddressMonths
        netWorth
        grossMonthlyIncome
        monthlyHousingPayment
        employerName
        employerAddress
        employerAddress2
        employerCity
        employerState
        employerZip
        employmentStatus
        workPhone
        employerEmail
        timeAtJobYears
        timeAtJobMonths
        mobilePhone
        signature
        signatureDate
        dlUploaded
        email
        usCitizen
        commercialDriverMonths
        commercialDriverYears
        residentStatus
        providingGuaranty
        maritalStatus
        companyDriverYears
        ownerOperatorYears
        countryOfResidence
        ownershipType
        firstTimeFinancing
        nearestRelativeFirstName
        nearestRelativeLastName
        nearestRelativePhone
        nearestRelativeCity
        nearestRelativeZip
        prevAddress
        prevAddress2
        prevCity
        prevState
        prevCounty
        prevPostalCode
        reasonForDeclining
        yearsWithCdl
        personalGuaranteeSignature
        personalGuaranteeSignatureDate
        addendum
        ipAddress
      }
      mostRecentCreditReportBusiness {
        creditReportBusinessId
        externalReportId
        vendorOpportunityId
        reportType
        score
        machineReadableReport
        humanReadableReportDocumentId
        createdDateTime
      }
      mostRecentCreditReportOfac {
        creditReportBusinessId
        externalReportId
        vendorOpportunityId
        reportType
        score
        machineReadableReport
        humanReadableReportDocumentId
        createdDateTime
      }
      creditApplicationOwner {
        ...creditApplicationOwnerAttributes
      }
      creditApplicationContacts {
        ...creditApplicationOwnerAttributes
      }
      creditLineStatus
      truckType
      references {
        referenceId
        companyName
        contactName
        contactPhone
        accountNum
        originalLoanAmt
        percRevenue
        years
        months
        lineOfCredit
        creditLimit
        currentBalance
        monthlyPayment
        renewalDate
        referenceType
        contactEmail
        contactFax
        address
        city
        state
        zip
        county
        jobTitle
        startDate
        endDate
        industry
        salary
        monthlyRevenue
        paidPerMile
        percentGross
        averageBalance
        yearMakeModel
        paidOff
        trade
        securedBy
        physicalDamageDeduction
        liabilityCoverage
        productType
        okayToContact
        driverType
        futureEmployerSameAsCurrent
        monthlyMiles
        taxId
        yearBusinessEstablished
        custom1
        custom2
        custom3
        referenceIdx
        ownerId
      }
      locationId
      location {
        locationId
        locationName
        category
        accountDynamicsId
      }
      businessOrIndividual
      farmType
      usCitizen
      farmingStatus
      addressCounty
      agriculturalExempt
      annualMileage
      annualRevenue
      apContactEmail
      apContactName
      apContactPhone
      bankruptcyDate
      contractorLicenseNum
      currentOwnerStartBusiness
      downPaymentAmount
      employerAddress
      employerAddress2
      employerCity
      employerEmail
      employerName
      employerPhone
      employerState
      employerZip
      existingCustomer
      fleetOperation
      fleetSegments
      formId
      homeCounty
      inactiveMonths
      incomeAmountPerYearOther
      incomeSourceOther
      lienOrJudgement
      lienOrJudgementDetail
      numOfEmployees
      numberOfAdditions
      numberOfReplacements
      ocaType
      operatingLine
      periodOfEmploymentMonths
      periodOfEmploymentYears
      piecesOfTractorEqpt
      piecesOfTruckEqpt
      piecesOfUnitsPurchased
      prevAddress
      prevCity
      prevCounty
      prevPostalCode
      prevState
      previousEmployerName
      previousEmployerPeriodOfEmploymentYears
      primaryBankAccountNum
      primaryBankAverageBalance
      primaryBankContactName
      primaryBankName
      primaryBankPhone
      primaryCreditAccountNum
      primaryCreditAverageBalance
      primaryCreditContactEmail
      primaryCreditContactName
      primaryCreditName
      primaryCreditPhone
      priorBankruptcy
      salesTaxExempt
      seasonalBusiness
      termRequested
      timeAtAddressInMonths
      timeAtAddressInYears
      timeAtPrevAddressMonths
      timeAtPrevAddressYears
      totalAssets
      totalLiabilities
      yearsInBusiness
      pastExperience
      disclosure
      applicantType
      hadItemRepossessed
      repossessionDetail
      rentalHouse
      firstTimeBuyer
      addendum
      addendumContent
      insuranceWaiver
      insuranceWaiverContent
      providingInsurance
      purchaseTimeframe
      authorizedParties
      workingCapitalRequestedAmount
      averageDailyBalance
      workingCapitalOffer
      recertificationDate
      recertificationHistory
    }
  }
  ${creditApplicationOwnerFragment}
`;

export const QUERY_ACCOUNT_BY_VP_ID = gql`
  query ocaTemplateAccount($vendorProfileId: ID!) {
    accountByVPId(vendorProfileId: $vendorProfileId) {
      id
      name
      vendorProfileId
      accountType
    }
  }
`;

export const QUERY_PORTAL_CONFIGURATION = gql`
  query ($vendorProfileId: ID!, $types: [PortalConfigurationType]) {
    portalConfigurations(vendorProfileId: $vendorProfileId, types: $types) {
      portalConfigurationId
      vendorProfileId
      jsonDefinition
      type
    }
  }
`;

export const Q_VENDOR_PROFILE = gql`
  query ($id: ID, $dynamicsId: ID, $userProfileId: ID) {
    vendorProfile(id: $id, dynamicsId: $dynamicsId, userProfileId: $userProfileId) {
      id
      name
      dynamicsId
      showDocMgt
      showLenderDocsAccessMgmt
      showCreditApp
      showPsOptions
      showMarginTool
      showLenderSubmission
      showPortalCreditApp
      showPrequal
      industryGroupType
      role
      logo
      dcrPrescreenStatus
      rawDisclosureTermsPage1
      rawDisclosureTermsPage2
      showChat
      showTasks
      amountRequested
      showContract
      showRouteOneApp
      softPullInDealerPortal
      settingBusinessCreditReportPullInDealerPortal
      showNotes
      dcrDisclosureLanguage
      dcrDisclosureLanguageSpanish
      dcrDisclosureTerms
      dcrDisclosureTermsSpanish
      dcrDisclosureUseDefaultTerms
      ocaPersonalGuaranteeAgreementDisclosure
      ofacRedFlagComplianceAccess
      daysToCreditAppRecertification
      requireCreditAppRecertification
      ocaAutofillSettings
      ocaShowCookieConsentBanner
      creditPullSettings {
        vendorProfileCreditPullSettingsId
        vendorProfileId
        autoPull
        creditBureauType
      }
      lenderProfiles {
        id
        lenderName
        submissionMethod
        submissionReviewShowMappingModal
        lenderChannelCode
        lenderTypeCode
        showCustomApp
        draftApplicationsEnabled
        pdfTemplate
        lenderProfileId
        lenderAccountId
        lenderContactId
        isDisabled
        lenderUserProfile {
          id
          email
          dynamicsContactId
          firstName
          lastName
          fullName
          phoneNumber
          mobilePhoneNumber
          mugshot
          accountId
        }
        lenderAccount {
          id
          dynamicsAccountId
          vendorProfileId
          primaryContactId
          name
          address
          phone
          primaryContactName
          primaryContactPhone
          primaryContactEmail
        }
      }
    }
  }
`;

export const M_UPDATE_LENDER_WATERFALL = gql`
  mutation UpdateLenderWaterfall(
    $input: UpdateLenderWaterfallPrescreenResultInput!
    $lenderWaterfallPrescreenCriteriaResultId: ID!
  ) {
    updateLenderWaterfallPrescreenResult(
      input: $input
      lenderWaterfallPrescreenCriteriaResultId: $lenderWaterfallPrescreenCriteriaResultId
    ) {
      lenderWaterfallPrescreenCriteriaResultId
      lenderProfileId
      createdDateTime
      modifiedDateTime
      step
      vendorOpportunityId
      processInstanceId
    }
  }
`;

export const QUERY_ACTIVE_OCA_TEMPLATE = gql`
  query ($vendorProfileId: ID!, $isActive: Boolean) {
    getOcaTemplatesByVendorProfile(vendorProfileId: $vendorProfileId, isActive: $isActive) {
      ocaTemplateId
      name
      vendorProfileName
      jsonDefinition
      active
    }
  }
`;

export const GET_LENDER_PROFILES_WITH_OCA_ACCESS = gql`
  query GetLenderProfilesWithOcaAccess($accountId: ID!) {
    lenderProfilesWithOcaAccess(accountId: $accountId) {
      success
      data {
        lenderProfileId
        name
        logo
      }
    }
  }
`;

export const SAVE_PORTAL_CONFIGURATION = gql`
  mutation ($input: UpsertPortalConfigurationInput!) {
    savePortalConfiguration(input: $input)
  }
`;

export const M_UPDATE_VP = gql`
  mutation ($entityId: String!, $fieldsObj: UpdateVendorProfileInput!) {
    updateVendorProfile(entityId: $entityId, fieldsObj: $fieldsObj) {
      id
    }
  }
`;

export const UPDATE_OCA_TEMPLATE = gql`
  mutation ($input: UpsertOCATemplateInput!) {
    updateOCATemplate(input: $input)
  }
`;

export const UPSERT_TASK = gql`
  mutation ($input: UpsertTaskInput!) {
    upsertTask(input: $input)
  }
`;

export const QUERY_PROPOSALS = gql`
  query proposals($userProfileId: ID, $accountId: ID, $voId: ID, $creditSubmissionDynamicsId: ID) {
    proposals(
      userProfileId: $userProfileId
      accountId: $accountId
      voId: $voId
      creditSubmissionDynamicsId: $creditSubmissionDynamicsId
    ) {
      proposalId
      id
      productId
      rate
      riskTier
      amountRequested
      markupInternal
      markupDealer
      bizName
      proposalStage
      businessName
      creditProductIds
      vendorOpportunityId
      accountId
      dynamicsAccountId
      status
      createdDateTime
      gbbCalculatedOptions
      gbbProposedTerms
      residual
      type
      equipmentId
      quantity
      newUsed
      year
      make
      model
      additionalDescription
      equipmentPictureUrl
      serialNumberVin
      stockId
      mileage
      gbbProposedApr
      createdDateTime
      lenderType
      maxApprovedAmount
      financedAmount
      redirect
      equipmentDescription
      fastTrackId
      category
      leadSheetFormId
      paymentSelectorOptions {
        paymentSelectorOptionsId
        lenderProfileId
        lenderProgramId
        textHeader
        textPaymentOptions
        textPaymentOptionsFinePrint
        textAmount
        showInterestRate
        showBestInterestRate
        createdDateTime
        modifiedDateTime
      }
      paymentCards {
        productId
        productName
        productHeader
        term
        rate
        payment
        rateFactor
        riskTier
        amountMin
        amountMax
        docFee
        advancePayment
        markupInternal
        lender
        calculatedPayment {
          paymentAmountPerPeriod
        }
        lenderProgramId
      }
      lenderPaymentOptions {
        paymentId
        proposalId
        lenderId
        term
        residual
        lenderType
        payment
        paymentGroupId
        paymentCriteria
      }
      vendorOpportunity {
        equipmentDescription
      }
      contact {
        email
        firstName
        lastName
        fullName
        phoneNumber
        mugshot
        title
        availability
      }
      vendorProfile {
        id
        paymentSelectorAutomatic
        industryGroupType
        logo
        styles
        ocaForm
        altLanguageOca
        dcrDisclosureLanguage
        dcrDisclosureTerms
        dcrDisclosureLanguageSpanish
        dcrDisclosureTermsSpanish
        dcrDisclosureUseDefaultTerms
      }
    }
  }
`;

export const QUERY_INTEGRATION_SETTINGS_ACCOUNT_IS_ADMIN_OF = gql`
  query integrationSettingsAccountIsAdminOf($accountId: ID!) {
    integrationSettingsAccountIsAdminOf(accountId: $accountId) {
      integrationName
      integrationSettings
      integrationAdminAccountId
      accountLevel
      locationLevel
      userProfileLevel
      createdDateTime
      modifiedDateTime
    }
  }
`;

export const QUERY_IS_ACCOUNT_HAS_ISVs = gql`
  query HasAccountIntegrationSettingValue($accountId: String!, $integrationName: String!) {
    hasAccountIntegrationSettingValue(accountId: $accountId, integrationName: $integrationName) {
      result
    }
  }
`;
